import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { fmtDMY } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, Undo2, ChevronLeft, ChevronRight } from "lucide-react";

export function HolidayList() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [year, setYear] = useState(new Date().getFullYear());
  const [add, setAdd] = useState({ date: "", name: "" });
  const { data } = useQuery({
    queryKey: ["holidays", year],
    queryFn: async () => (await api.get(`/holidays?year=${year}`)).data,
  });
  const override = useMutation({
    mutationFn: (body) => api.put("/holidays/override", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["holidays"] });
      qc.invalidateQueries({ queryKey: ["roster-day"] });
      qc.invalidateQueries({ queryKey: ["weekly-timesheet"] });
      setAdd({ date: "", name: "" });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  if (!data) return null;
  const ruleOff = data.rule === "off";
  return (
    <div className="mt-4 border-t pt-3" data-testid="holiday-list">
      <div className="mb-2 flex items-center gap-2">
        <span className="text-xs font-semibold">{t("Public holidays — {{where}}", { where: data.region ? `${data.country} / ${data.region}` : data.country })}</span>
        <span className="ml-auto flex items-center gap-1 text-xs">
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setYear((y) => y - 1)} data-testid="holiday-year-prev"><ChevronLeft className="h-3.5 w-3.5" /></Button>
          <b data-testid="holiday-year">{year}</b>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setYear((y) => y + 1)} data-testid="holiday-year-next"><ChevronRight className="h-3.5 w-3.5" /></Button>
        </span>
      </div>
      <div className="max-h-72 space-y-1 overflow-y-auto pr-1">
        {data.holidays.map((h) => (
          <div key={h.date} className={`flex flex-wrap items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs ${h.removed ? "border-dashed opacity-60" : "bg-card"}`} data-testid={`holiday-row-${h.date}`}>
            <span className="w-[86px] font-mono text-muted-foreground">{fmtDMY(h.date)}</span>
            <span className={`font-medium ${h.removed ? "line-through" : ""}`}>{h.name}</span>
            {h.source === "custom" && <span className="rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-800">{t("added")}</span>}
            {h.removed && <span className="rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700">{t("does not apply")}</span>}
            <span className="ml-auto flex items-center gap-2">
              {!h.removed && ruleOff && (
                <label className="flex items-center gap-1 text-[11px] text-muted-foreground" title={t("On: cleaners keep their normal roster on this holiday (timesheet still shows the holiday)")}>
                  <Switch checked={!h.keep_roster} onCheckedChange={() => override.mutate({ date: h.date, action: "rostered" })} data-testid={`holiday-unroster-${h.date}`} className="scale-75" />
                  {h.keep_roster ? t("normal roster") : t("cleaners off")}
                </label>
              )}
              {h.removed ? (
                <Button variant="ghost" size="sm" className="h-6 gap-1 px-2 text-[11px]" onClick={() => override.mutate({ date: h.date, action: "reset" })} data-testid={`holiday-restore-${h.date}`}>
                  <Undo2 className="h-3.5 w-3.5" /> {t("Reactivate")}
                </Button>
              ) : (
                <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => override.mutate({ date: h.date, action: "remove" })} title={t("Doesn't apply to us")} data-testid={`holiday-remove-${h.date}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </span>
          </div>
        ))}
        {data.holidays.length === 0 && <p className="text-xs italic text-muted-foreground">{t("No public holidays found for this year.")}</p>}
      </div>
      <div className="mt-2 flex flex-wrap items-end gap-2" data-testid="holiday-add">
        <Input type="date" value={add.date} onChange={(e) => setAdd({ ...add, date: e.target.value })} className="h-8 w-40" data-testid="holiday-add-date" />
        <Input value={add.name} onChange={(e) => setAdd({ ...add, name: e.target.value })} placeholder={t("Holiday name (e.g. local anniversary)")} className="h-8 flex-1 min-w-[160px]" data-testid="holiday-add-name" />
        <Button size="sm" variant="outline" className="h-8 gap-1" disabled={!add.date || !add.name.trim() || override.isPending} onClick={() => override.mutate({ date: add.date, action: "add", name: add.name })} data-testid="holiday-add-btn">
          <Plus className="h-3.5 w-3.5" /> {t("Add missed holiday")}
        </Button>
      </div>
    </div>
  );
}
